import { Link, useNavigate } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { User, SignOut, Crown, ChartBar, Tray } from "@phosphor-icons/react";

export const PLAN_LABELS = {
  free: "Free",
  trial: "Free Trial",
  weekly: "Weekly",
  monthly: "Monthly",
  yearly: "Yearly",
};

/** Trial countdown chip / paid plan badge shown left of the avatar. */
export const PlanBadge = ({ user }) => (
  <>
    {user.plan === "trial" && user.days_remaining !== null && user.days_remaining !== undefined && (
      <Link
        to="/pricing"
        className="hidden md:inline-flex items-center gap-1 px-2 py-1 border border-foreground bg-foreground text-background font-mono text-[10px] uppercase tracking-widest hover:bg-[hsl(var(--accent))] hover:border-[hsl(var(--accent))] transition-colors"
        data-testid="header-trial-chip"
        title="Click to view paid plans"
      >
        <Crown size={10} weight="fill" />
        Trial · {user.days_remaining}d left
      </Link>
    )}
    {user.plan !== "free" && user.plan !== "trial" && (
      <span
        className="hidden md:inline-flex items-center gap-1 px-2 py-1 border border-[hsl(var(--accent))] text-[hsl(var(--accent))] font-mono text-[10px] uppercase tracking-widest"
        data-testid="header-plan-badge"
      >
        <Crown size={10} weight="fill" />
        {PLAN_LABELS[user.plan]}
      </span>
    )}
  </>
);

/** Avatar dropdown: profile, stories, plan, admin inquiries, sign out. */
export const UserMenu = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const initials = (user?.name || user?.email || "?")
    .split(/[ @.]/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase())
    .join("");

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="flex items-center gap-2 h-9 px-2 border border-border hover:border-foreground"
          data-testid="profile-menu-trigger"
        >
          {user.picture ? (
            <img src={user.picture} alt="" className="w-7 h-7 object-cover" />
          ) : (
            <div className="w-7 h-7 bg-foreground text-background flex items-center justify-center font-mono text-[10px] font-bold">
              {initials}
            </div>
          )}
          <div className="hidden sm:block text-left leading-tight">
            <div className="text-xs font-semibold truncate max-w-[120px]">
              {user.name || user.email.split("@")[0]}
            </div>
            <div className="text-[9px] font-mono uppercase tracking-widest text-muted-foreground">
              {PLAN_LABELS[user.plan] || "Free"}
            </div>
          </div>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="rounded-none w-56">
        <div className="px-3 py-2 border-b border-border">
          <div className="text-xs font-mono text-muted-foreground truncate">{user.email}</div>
        </div>
        <DropdownMenuItem
          onClick={() => navigate("/profile")}
          className="rounded-none font-mono text-sm"
          data-testid="menu-profile"
        >
          <User size={14} className="mr-2" />
          Profile
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => navigate("/app")}
          className="rounded-none font-mono text-sm"
          data-testid="menu-stories"
        >
          <ChartBar size={14} className="mr-2" />
          Stories
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => navigate("/pricing")}
          className="rounded-none font-mono text-sm"
          data-testid="menu-pricing"
        >
          <Crown size={14} className="mr-2" />
          Manage plan
        </DropdownMenuItem>
        {user.role === "admin" && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => navigate("/admin/inquiries")}
              className="rounded-none font-mono text-sm"
              data-testid="menu-admin-inquiries"
            >
              <Tray size={14} className="mr-2" />
              Inquiries
            </DropdownMenuItem>
          </>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={onLogout}
          className="rounded-none font-mono text-sm text-destructive"
          data-testid="menu-logout"
        >
          <SignOut size={14} className="mr-2" />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
